import { environment } from "../../environments/environment";

/**
 * Constants for group names used for authorization
 * Values are now read from environment configuration
 */
export const GroupNames = {
  TURBINE_USERS: environment.groups.TurbineRoleGroups,
  RAM_PLANNERS: environment.groups.RamRoleGroups,
  TECHNICAL_LETTER_VIEWERS: environment.groups.LetterUserRoleGroups,
  TECHNICAL_LETTER_ADMINS: environment.groups.LetterAdminRoleGroups,
} as const;

/**
 * Type for group names
 */
export type GroupName = (typeof GroupNames)[keyof typeof GroupNames];

/**
 * Utility class for handling group operations
 */
export class GroupUtils {
  /**
   * Splits a group name string into individual groups
   * @param groupName - The group name string (may contain comma-separated groups)
   * @returns Array of individual group names
   */
  static splitGroups(groupName: string): string[] {
    return groupName
      .split(",")
      .map((group) => group.trim())
      .filter((group) => group.length > 0);
  }

  /**
   * Gets all individual groups from a GroupName constant
   * @param groupName - The GroupName constant
   * @returns Array of individual group names
   */
  static getIndividualGroups(groupName: GroupName): string[] {
    return this.splitGroups(groupName);
  }

  /**
   * Gets all unique individual groups from multiple GroupName constants
   * @param groupNames - Array of GroupName constants
   * @returns Array of unique individual group names
   */
  static getAllIndividualGroups(groupNames: GroupName[]): string[] {
    const allGroups = groupNames.flatMap((groupName) =>
      this.getIndividualGroups(groupName)
    );
    return Array.from(new Set(allGroups));
  }
}
